import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Instagram, Facebook } from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();

  const navLinks = [
    { path: '/', key: 'home' },
    { path: '/galerie', key: 'gallery' },
    { path: '/boutique', key: 'boutique' },
    { path: '/commandes', key: 'orders' },
    { path: '/contact', key: 'contact' },
    { path: '/emploi', key: 'emploi' },
  ];

  const boutiques = [
    { name: 'Martigny', address: 'Rue des Petits-Epineys 3, 1920 Martigny', phone: '027 722 27 50', maps: 'https://maps.google.com/?q=Rue+des+Petits-Epineys+3+1920+Martigny' },
    { name: 'Verbier', address: 'Rte des Creux 6, 1936 Verbier', phone: '027 565 57 50', maps: 'https://maps.google.com/?q=Rte+des+Creux+6+1936+Verbier' },
    { name: 'Sion', address: 'Av. du Ritz 33, 1950 Sion', phone: '027 323 23 16', maps: 'https://maps.google.com/?q=Av+du+Ritz+33+1950+Sion' },
  ];

  return (
    <footer data-testid="footer" className="bg-ginkgo-900 text-white/80">
      {/* Main footer */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">

          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <svg width="24" height="24" viewBox="0 0 100 100" fill="none" aria-hidden="true">
                <path d="M50 90 C50 90 20 65 20 40 C20 22 33 10 50 10 C67 10 80 22 80 40 C80 65 50 90 50 90Z" fill="#C5A065" opacity="0.9"/>
                <path d="M50 90 C50 90 35 68 35 45 C35 30 41 18 50 18 C59 18 65 30 65 45 C65 68 50 90 50 90Z" fill="white" opacity="0.3"/>
                <line x1="50" y1="90" x2="50" y2="15" stroke="white" strokeWidth="1.5" opacity="0.5"/>
              </svg>
              <span className="font-serif text-white text-lg font-medium">Les Noces de Ginkgo</span>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/lesnocesdeginkgo"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={15} />
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={15} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-white text-xs uppercase tracking-widest font-medium mb-6">
              {t('footer.nav_title')}
            </h3>
            <ul className="space-y-3">
              {navLinks.map(({ path, key }) => (
                <li key={path}>
                  <Link
                    to={path}
                    data-testid={`footer-nav-${key}`}
                    className="text-sm text-white/60 hover:text-gold transition-colors"
                  >
                    {t(`nav.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white text-xs uppercase tracking-widest font-medium mb-6">
              {t('footer.contact_title')}
            </h3>
            <div className="space-y-5">
              {boutiques.map((b) => (
                <div key={b.name} className="space-y-1">
                  <p className="text-white text-xs uppercase tracking-wider font-medium">{b.name}</p>
                  <div className="flex items-start gap-2 text-white/60">
                    <MapPin size={12} className="mt-0.5 flex-shrink-0 text-gold" />
                    <a href={b.maps} target="_blank" rel="noopener noreferrer" className="text-xs hover:text-white/90 transition-colors leading-relaxed">
                      {b.address}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-white/60">
                    <Phone size={12} className="flex-shrink-0 text-gold" />
                    <a href={`tel:+41${b.phone.replace(/\s/g,'').substring(1)}`} className="text-xs hover:text-white/90 transition-colors">
                      {b.phone}
                    </a>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 text-white/60 pt-1">
                <Mail size={12} className="flex-shrink-0 text-gold" />
                <a href="mailto:info@lesnocesdeginkgo.ch" className="text-xs hover:text-white/90 transition-colors">
                  info@lesnocesdeginkgo.ch
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} Les Noces de Ginkgo — {t('footer.rights')}
          </p>
          <p className="text-xs text-white/30">{t('footer.made_in')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
